import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selTokens } from "store/slices";
import ImageCropModal from "./../app/components/common/cropper/ImageCropModal";
import { getCroppedImg } from "./../util/imageCrop";
import {
  downloadFileAPI,
  uploadAPI,
  uploadMultipleAPI,
} from "api/fileManagerApi";

const initFileData = { file: null, url: "", fileId: "" };
const initMultipleFileData = [];

export const FilemanagerAcceptTypes = {
  image: "image/gif, image/jpeg, image/png",
  pdf: "application/pdf",
  text: "text/plain",
  excel: ".xls, .xlsx",
  video: "video/mp4, video/avi",
};

export const useFileManager = (props) => {
  const [acceptType, setAcceptType] = useState(null);
  const [fileData, setFileData] = useState(initFileData);
  const [arrayFileData, setArrayFileData] = useState(initMultipleFileData);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [isMultiple, setIsMultiple] = useState(false);
  const { token: Authorization } = useSelector(selTokens);
  const inputRef = useRef(null);

  useEffect(() => {
    if (acceptType) {
      inputRef.current?.click();
    }
  }, [acceptType]);

  function onFileChange(event) {
    let file = event.target?.files[0];
    if (file && acceptType === "image") {
      // Show crop modal for images
      let url = URL.createObjectURL(file);
      setTempImageSrc(url);
      setShowCropModal(true);
      setIsMultiple(false);
    } else {
      // For non-images, proceed normally
      let url = URL.createObjectURL(file);
      setFileData((prevState) => ({ ...prevState, file, url }));
    }
  }

  function onFilesChange(event) {
    let files = [];
    for (const file of Array.from(event.target?.files)) {
      if (acceptType === "image") {
        // For images in multiple mode, we'll need to crop each one
        // For now, let's handle the first one
        let url = URL.createObjectURL(file);
        setTempImageSrc(url);
        setShowCropModal(true);
        setIsMultiple(true);
        break; // Handle one at a time
      } else {
        const plain = { file, url: URL.createObjectURL(file) };
        files.push(plain);
      }
    }
    if (acceptType !== "image") {
      setArrayFileData((prev) => [...prev, ...files]);
    }
  }

  const handleCropComplete = async (croppedAreaPixels) => {
    try {
      const croppedImage = await getCroppedImg(tempImageSrc, croppedAreaPixels);
      const croppedUrl = URL.createObjectURL(croppedImage);

      if (isMultiple) {
        setArrayFileData((prev) => [
          ...prev,
          { file: croppedImage, url: croppedUrl },
        ]);
      } else {
        setFileData({ file: croppedImage, url: croppedUrl, fileId: "" });
      }

      setShowCropModal(false);
      setTempImageSrc(null);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempImageSrc(null);
    // Reset file input
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const openFileManager = (type = "image") => {
    if (!inputRef?.current) return;
    if (acceptType === type) return inputRef.current?.click();
    setAcceptType(type);
  };

  const onClearFile = () => {
    setFileData(initFileData);
  };

  const onClearMultipleFile = () => {
    setArrayFileData(initMultipleFileData);
  };

  const addMultipleFiles = (files = []) => {
    const fileList = Array.isArray(files) ? files : [files];
    const mappedFiles = fileList
      .filter(Boolean)
      .map((file) => ({ file, url: URL.createObjectURL(file) }));

    if (!mappedFiles.length) return;
    setArrayFileData((prev) => [...prev, ...mappedFiles]);
  };

  const onRemoveFile = (url) => {
    let filteredFiles = arrayFileData.filter((f) => f.url !== url);
    setArrayFileData(filteredFiles);
  };

  const onUpload = async () => {
    let { file } = fileData;
    if (file) {
      try {
        let res = await uploadAPI(file);
        if (res.message.id) {
          return res.message;
        }
        throw res.message;
      } catch (error) {
        console.error("Upload error:", error);
      }
    }
  };

  const onMultipleUpload = async (
    is_public = true,
    is_airline_public = false
  ) => {
    let files = arrayFileData.map((f) => f?.file);
    if (files) {
      try {
        let res = await uploadMultipleAPI(
          files,
          (progress) => {},
          is_public,
          is_airline_public
        );
        if (res?.message?.length > 0) {
          return res;
        }
        throw "Error: File upload failed";
      } catch (error) {
        console.error("Multiple upload error:", error);
        throw error;
      }
    }
  };

  const onFetchFile = async (fileId) => {
    try {
      let res = await downloadFileAPI(fileId, true, { Authorization });
      let url = URL.createObjectURL(res);
      return url;
    } catch (error) {
      let errors = {
        403: "Download Permission Denied!",
      };
      console.error("Fetch error:", error);
    }
  };

  const onDownload = async (fileId, is_public = true) => {
    try {
      let res = await downloadFileAPI(fileId, is_public);
      let url = URL.createObjectURL(res);
      window.open(url, "_blank");
      return url;
    } catch (error) {
      let errors = {
        403: "Download Permission Denied!",
      };
      console.error("Download error:", error);
    }
  };

  const FileManager = ({ children, multiple = false }) => {
    return (
      <>
        <input
          accept={FilemanagerAcceptTypes[acceptType]}
          type="file"
          onChange={multiple ? onFilesChange : onFileChange}
          autoFocus
          style={{ display: "none" }}
          ref={inputRef}
          multiple={multiple}
        />
        {children}

        {/* Image Crop Modal */}
        <ImageCropModal
          open={showCropModal}
          onClose={handleCropCancel}
          imageSrc={tempImageSrc}
          onCropComplete={handleCropComplete}
          aspectRatio={1} // 1:1 for circle
        />
      </>
    );
  };

  return {
    FileManager,
    fileData,
    filesData: arrayFileData,
    onClearFile,
    openFileManager,
    onUpload,
    onDownload,
    onFetchFile,
    onFileChange,
    onFilesChange,
    onMultipleUpload,
    onClearMultipleFile,
    onRemoveFile,
    addMultipleFiles,
    showCropModal,
  };
};
